
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Search, Users, Bell, Building, PlusCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import CandidateCard from '@/components/recruiter/CandidateCard';
import JobPostForm from '@/components/recruiter/JobPostForm';
import ProjectRequirementForm from '@/components/recruiter/ProjectRequirementForm';

const RecruiterDashboard = () => {
  const { user, profile, isLoading, signOut } = useAuth();
  const [candidates, setCandidates] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading_candidates, setIsLoadingCandidates] = useState(false);
  const { toast } = useToast();

  // Fetch job seekers profiles
  useEffect(() => {
    if (!user) return;
    
    const fetchCandidates = async () => {
      setIsLoadingCandidates(true);
      try {
        // Get all job seeker profiles
        const { data, error } = await supabase
          .from('profiles')
          .select(`
            id,
            first_name,
            last_name,
            job_seeker_profiles!inner(
              headline,
              location,
              current_position,
              years_of_experience
            )
          `)
          .eq('role', 'job_seeker');

        if (error) throw error;
        setCandidates(data || []);
      } catch (error: any) {
        toast({
          title: "Failed to load candidates",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setIsLoadingCandidates(false);
      }
    };

    fetchCandidates();
  }, [user, toast]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-forest">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  if (profile?.role !== 'recruiter') {
    return <Navigate to="/onboarding" />;
  }

  const filteredCandidates = candidates.filter(candidate => {
    const fullName = `${candidate.first_name || ''} ${candidate.last_name || ''}`.toLowerCase();
    const position = candidate.job_seeker_profiles?.current_position?.toLowerCase() || '';
    const location = candidate.job_seeker_profiles?.location?.toLowerCase() || '';
    
    const query = searchQuery.toLowerCase();
    
    return fullName.includes(query) || 
           position.includes(query) || 
           location.includes(query);
  });

  // Placeholder data
  const profileCompletion = 20;
  const notificationCount = 2;

  return (
    <div className="min-h-screen bg-forest p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Recruiter Dashboard</h1>
            <p className="text-white/70">Welcome, {profile?.first_name || 'User'}</p>
          </div>
          <Button 
            variant="outline" 
            className="text-white border-mint/30 hover:bg-mint/10"
            onClick={signOut}
          >
            Sign Out
          </Button>
        </div>

        {/* Profile Completion Card */}
        <Card className="bg-forest-light border border-mint/20 text-white">
          <CardHeader>
            <CardTitle>Company Profile Completion</CardTitle>
            <CardDescription className="text-white/70">
              Complete your company profile to attract more candidates
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-white/70">Progress</span>
              <span className="text-mint font-semibold">{profileCompletion}%</span>
            </div>
            <Progress value={profileCompletion} className="bg-forest h-2" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
              <Button variant="outline" className="bg-transparent border-mint/30 text-white hover:bg-mint/10 flex justify-start gap-2">
                <Building className="text-mint" />
                Complete Company Details
              </Button>
              <Button variant="outline" className="bg-transparent border-mint/30 text-white hover:bg-mint/10 flex justify-start gap-2">
                <Users className="text-mint" />
                Add Team Members
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Dashboard Tabs */}
        <Tabs defaultValue="candidates" className="w-full">
          <TabsList className="bg-forest-light border border-mint/20 text-white mb-4">
            <TabsTrigger value="candidates" className="data-[state=active]:bg-mint/20 text-white">
              Browse Candidates
            </TabsTrigger>
            <TabsTrigger value="post-jobs" className="data-[state=active]:bg-mint/20 text-white">
              Post Jobs
            </TabsTrigger>
            <TabsTrigger value="projects" className="data-[state=active]:bg-mint/20 text-white">
              Project Requirements
            </TabsTrigger>
          </TabsList>

          {/* Candidates Tab */}
          <TabsContent value="candidates" className="mt-0">
            <Card className="bg-forest-light border border-mint/20 text-white">
              <CardHeader>
                <CardTitle className="text-xl">Find Candidates</CardTitle>
                <CardDescription className="text-white/70">
                  Search for candidates by skills, experience, or location
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-mint" />
                  <input
                    className="w-full bg-forest border border-mint/30 rounded-md py-2 pl-10 pr-4 text-white placeholder:text-white/50"
                    placeholder="Search candidates by name, position, or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {isLoading_candidates ? (
                  <div className="text-center py-10">
                    <p className="text-white/70">Loading candidates...</p>
                  </div>
                ) : filteredCandidates.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredCandidates.map(candidate => (
                      <CandidateCard key={candidate.id} candidate={candidate} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <p className="text-white/70">No candidates found matching your search criteria</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Post Jobs Tab */}
          <TabsContent value="post-jobs" className="mt-0">
            <Card className="bg-forest-light border border-mint/20 text-white">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-xl">Post Jobs</CardTitle>
                    <CardDescription className="text-white/70">
                      Create new job postings for candidates
                    </CardDescription>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="bg-mint text-forest hover:bg-mint/90 flex items-center gap-2">
                        <PlusCircle className="h-4 w-4" />
                        New Job
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-forest-light border border-mint/20 text-white max-w-3xl">
                      <DialogHeader>
                        <DialogTitle>Create New Job</DialogTitle>
                        <DialogDescription className="text-white/70">
                          Fill in the details to post a new job opportunity
                        </DialogDescription>
                      </DialogHeader>
                      <JobPostForm />
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-center py-10">
                  <p className="text-white/70 mb-4">No jobs posted yet</p>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="bg-mint text-forest hover:bg-mint/90">
                        Post Your First Job
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-forest-light border border-mint/20 text-white max-w-3xl">
                      <DialogHeader>
                        <DialogTitle>Create New Job</DialogTitle>
                        <DialogDescription className="text-white/70">
                          Fill in the details to post a new job opportunity
                        </DialogDescription>
                      </DialogHeader>
                      <JobPostForm />
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Project Requirements Tab */}
          <TabsContent value="projects" className="mt-0">
            <Card className="bg-forest-light border border-mint/20 text-white">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-xl">Project Requirements</CardTitle>
                    <CardDescription className="text-white/70">
                      Create project-based requirements for candidates
                    </CardDescription>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="bg-mint text-forest hover:bg-mint/90 flex items-center gap-2">
                        <PlusCircle className="h-4 w-4" />
                        New Project
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-forest-light border border-mint/20 text-white max-w-3xl">
                      <DialogHeader>
                        <DialogTitle>Create Project Requirement</DialogTitle>
                        <DialogDescription className="text-white/70">
                          Define the details for a new project requirement
                        </DialogDescription>
                      </DialogHeader>
                      <ProjectRequirementForm />
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-center py-10">
                  <p className="text-white/70 mb-4">No project requirements posted yet</p>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="bg-mint text-forest hover:bg-mint/90">
                        Post Your First Project Requirement
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-forest-light border border-mint/20 text-white max-w-3xl">
                      <DialogHeader>
                        <DialogTitle>Create Project Requirement</DialogTitle>
                        <DialogDescription className="text-white/70">
                          Define the details for a new project requirement
                        </DialogDescription>
                      </DialogHeader>
                      <ProjectRequirementForm />
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Notifications */}
        <Card className="bg-forest-light border border-mint/20 text-white">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xl">Notifications</CardTitle>
            <Button variant="ghost" size="sm" className="text-mint hover:bg-mint/10 h-8 w-8 p-0">
              <Bell className="h-5 w-5" />
              <span className="sr-only">Notifications</span>
            </Button>
          </CardHeader>
          <CardContent>
            {notificationCount > 0 ? (
              <div className="space-y-4">
                <div className="border-b border-mint/10 pb-3">
                  <p className="font-medium text-white">New candidate match</p>
                  <p className="text-sm text-white/70">A new candidate matches your search criteria</p>
                  <p className="text-xs text-mint mt-2">1 day ago</p>
                </div>
                <div>
                  <p className="font-medium text-white">Welcome!</p>
                  <p className="text-sm text-white/70">Welcome to Skhy AI Consultancy</p>
                  <p className="text-xs text-mint mt-2">2 days ago</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-white/70">No notifications yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RecruiterDashboard;
